import React, { useState, useEffect, useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Select,
  MenuItem,
  InputLabel,
  Checkbox,
  Alert,
  CircularProgress,
  Chip,
  Avatar,
} from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { ptBR } from "date-fns/locale";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../../../shared/context/auth-context";
import { ClinicaContext } from "../../../../shared/context/clinica-context";
import { useHttpClient } from "../../../../shared/hooks/http-hook";
import { useLoading } from "../../../../shared/context/LoadingContext";
import "./ModalConfirmarAgendamento.scss";
import toast from "react-hot-toast";
import { dadosFinanceirosTratamentoIsolado } from "../../../../shared/util/dadosFinanceirosTratamentoIsolado";

function ModalConfirmarAgendamento({
  open,
  onClose,
  agendamento: agendamentoProp,
  agendamentoId,
  onUpdate,
}) {
  const auth = useContext(AuthContext);
  const clinica = useContext(ClinicaContext);
  const { sendRequest } = useHttpClient();
  const { startLoading, stopLoading } = useLoading();
  const navigate = useNavigate();

  const [fetchLoading, setFetchLoading] = useState(!agendamentoProp);
  const [agendamento, setAgendamento] = useState(agendamentoProp);

  // Estados principais
  const [tipoConversao, setTipoConversao] = useState("PROCEDIMENTO");
  const [dataAgendamento, setDataAgendamento] = useState(
    agendamentoProp
      ? new Date(agendamentoProp.data_inicio_execucao)
      : new Date(),
  );
  const [dataFimAgendamento, setDataFimAgendamento] = useState(
    agendamentoProp ? new Date(agendamentoProp.data_fim_execucao) : new Date(),
  );
  const [observacoes, setObservacoes] = useState("");

  // Estados para Tratamento Isolado
  const [tratamentos, setTratamentos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [procedimentoSelecionado, setProcedimentoSelecionado] = useState("");
  const [adicionarEntrada, setAdicionarEntrada] = useState(false);

  // Determinar se usa tratamentos ou categorias
  const usaTratamentos =
    clinica?.clinica?.configuracoes?.configuracoes?.tipoAgendamento?.tipo ===
    "Tratamento";

  useEffect(() => {
    if (open && agendamentoId && !agendamentoProp) {
      fetchAgendamento();
    }
  }, [open, agendamentoId]);

  useEffect(() => {
    if (open && tipoConversao === "TRATAMENTO_ISOLADO") {
      // Lógica simplificada:
      // 1. Se clínica usa Tratamento → sempre busca tratamentos
      // 2. Se clínica usa Categoria:
      //    - Com entrada → busca tratamentos
      //    - Sem entrada → busca categorias
      if (usaTratamentos) {
        // Clínica tipo "Tratamento" → sempre tratamentos
        if (tratamentos.length === 0) {
          fetchTratamentos();
        }
      } else {
        // Clínica tipo "Categoria"
        if (adicionarEntrada) {
          // Com entrada → busca tratamentos
          if (tratamentos.length === 0) {
            fetchTratamentos();
          }
        } else {
          // Sem entrada → busca categorias
          if (categorias.length === 0) {
            fetchCategorias();
          }
        }
      }
    }
  }, [open, tipoConversao, usaTratamentos, adicionarEntrada]);

  const fetchAgendamento = async () => {
    setFetchLoading(true);
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agendamentosprovisorio/single/${agendamentoId}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      setAgendamento(responseData.agendamento);
      setDataAgendamento(
        new Date(responseData.agendamento.data_inicio_execucao),
      );
      setDataFimAgendamento(
        new Date(responseData.agendamento.data_fim_execucao),
      );
    } catch (err) {
      console.error("Erro ao buscar agendamento:", err);
    } finally {
      setFetchLoading(false);
    }
  };

  const fetchTratamentos = async () => {
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentos/clinica/${clinica.clinica._id}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      const tratamentosOrdenados = responseData.tratamentos.sort((a, b) =>
        a.designacao.localeCompare(b.designacao),
      );

      setTratamentos(tratamentosOrdenados);
    } catch (err) {
      console.error("Erro ao buscar tratamentos:", err);
    }
  };

  const fetchCategorias = async () => {
    try {
      const responseData = await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/categoriastratamento/clinica/${clinica.clinica._id}`,
        "GET",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      const categoriasOrdenadas = responseData.categoriasTratamento.sort(
        (a, b) => a.categoria.localeCompare(b.categoria),
      );

      setCategorias(categoriasOrdenadas);
    } catch (err) {
      console.error("Erro ao buscar categorias:", err);
    }
  };

  const handleProcedimento = () => {
    // Navegar para página de criação de avaliação
    const link = `/avaliacao/new/${agendamento.cliente.id}-agprov-${agendamento.id}`;
    navigate(link);
    onClose();
  };

  const handleTratamentoIsolado = async () => {
    if (!procedimentoSelecionado) {
      alert(
        `Por favor, selecione ${
          usaTratamentos ? "um tratamento" : "uma categoria"
        }`,
      );
      return;
    }

    startLoading();

    try {
      // Buscar categoria financeira e caixa
      const [categoriasResponse, caixasResponse] = await Promise.all([
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/categoriasfinanceiro/clinica/entrada/${clinica.clinica._id}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        ),
        sendRequest(
          `${process.env.REACT_APP_BACKEND_LINK}/api/caixas/clinica/${clinica.clinica._id}`,
          "GET",
          null,
          { Authorization: "Bearer " + auth.token },
        ),
      ]);

      /* Lança com uma frase que diz o que falta e onde se cria. Antes, uma
         clínica sem a caixa «Clínica» ou sem a categoria «Tratamento
         Isolado» rebentava mais abaixo com «Cannot read properties of
         undefined (reading '_id')». */
      const { caixa: tempCaixa, categoria: tempCategoriaFinanceiro } =
        dadosFinanceirosTratamentoIsolado(caixasResponse, categoriasResponse);

      const formData = new FormData();

      const tratamentoIsoladoData = {
        data_inicio_execucao: dataAgendamento,
        data_fim_execucao: dataFimAgendamento,
        medico: agendamento.medico.id || agendamento.medico._id,
        clinica: clinica.clinica._id,
        cliente: agendamento.cliente.id || agendamento.cliente._id,
        observacoesAgendamento: observacoes || "",
        relatorio: "",
        agendadoPor: auth.userId,
        nomeQuemAgendou: auth.name,
        agendamentoProvisorioId: agendamento.id,
        statusAgendamentoProvisorio: "AGENDADO",
      };

      if (usaTratamentos || adicionarEntrada) {
        tratamentoIsoladoData.tratamento = procedimentoSelecionado;
      } else {
        tratamentoIsoladoData.categoriaTratamento = procedimentoSelecionado;
      }

      const entradaData = {
        tipo: "reembolso",
        clinica: clinica.clinica._id,
        observacoes: "",
        dataLimitePagamento: new Date(),
        categoria: tempCategoriaFinanceiro._id,
        caixa: tempCaixa._id,
        cliente: agendamento.cliente.id || agendamento.cliente._id,
      };

      formData.append(
        "tratamentoIsoladoData",
        JSON.stringify(tratamentoIsoladoData),
      );
      formData.append("entradaData", JSON.stringify(entradaData));
      formData.append("nomeCliente", agendamento.cliente.nome);
      formData.append("nomeMedico", agendamento.medico.nome);
      formData.append("comEntrada", adicionarEntrada);

      // 1️⃣ PRIMEIRA REQUISIÇÃO: Criar Tratamento Isolado
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/tratamentoIsolados/${auth.userId}`,
        "POST",
        formData,
        {
          Authorization: "Bearer " + auth.token,
        },
      );

      // 2️⃣ SEGUNDA REQUISIÇÃO: Confirmar Agendamento Provisório
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agendamentosprovisorio/confirmar/${agendamento.id}`,
        "PATCH",
        null,
        {
          Authorization: "Bearer " + auth.token,
        },
      );
      toast.sucess("Tratamento Isolado agendado com sucesso!");
      onUpdate?.();
      onClose();
    } catch (err) {
      /* No ecrã, e não só na consola: a mensagem do
         `dadosFinanceirosTratamentoIsolado` diz exactamente o que falta e onde
         se cria, e morria aqui sem ninguém a ler. */
      console.error("Erro ao criar tratamento isolado:", err);
      toast.error(err.message || "Não foi possível criar o tratamento isolado.");
    } finally {
      stopLoading();
    }
  };

  const handleAgendamentoDireto = async () => {
    startLoading();

    try {
      const formData = new FormData();

      const marcacao = {
        cliente: agendamento.cliente.id || agendamento.cliente._id,
        data_inicio_execucao: dataAgendamento,
        data_fim_execucao: dataFimAgendamento,
        avaliacao: true,
        agendadoPor: auth.userId,
        nomeQuemAgendou: auth.name,
        observacoesAgendamento: observacoes || "",
        agendamentoProvisorioId: agendamento.id,
        statusAgendamentoProvisorio: "AGENDADO",
      };

      formData.append(
        "medico",
        agendamento.medico.id || agendamento.medico._id,
      );
      formData.append("nomeMedico", agendamento.medico.nome);
      formData.append("marcacao", JSON.stringify(marcacao));
      formData.append("nomeCliente", agendamento.cliente.nome);

      const endpoint = usaTratamentos
        ? `${process.env.REACT_APP_BACKEND_LINK}/api/agendatratamento/efetuarmarcacao/${clinica.clinica._id}`
        : `${process.env.REACT_APP_BACKEND_LINK}/api/agenda/efetuarmarcacao/${clinica.clinica._id}`;

      // 1️⃣ PRIMEIRA REQUISIÇÃO: Criar Agenda/AgendaTratamento
      await sendRequest(endpoint, "PATCH", formData, {
        Authorization: "Bearer " + auth.token,
      });

      // 2️⃣ SEGUNDA REQUISIÇÃO: Confirmar Agendamento Provisório
      await sendRequest(
        `${process.env.REACT_APP_BACKEND_LINK}/api/agendamentosprovisorio/confirmar/${agendamento.id}`,
        "PATCH",
        null,
        {
          "Content-Type": "application/json",
          Authorization: "Bearer " + auth.token,
        },
      );

      toast.success("agendamento efetuado com sucesso!");
      onUpdate?.();
      onClose();
    } catch (err) {
      console.error("Erro ao criar agendamento:", err);
    } finally {
      stopLoading();
    }
  };

  const handleSubmit = () => {
    if (tipoConversao === "PROCEDIMENTO") {
      handleProcedimento();
    } else if (tipoConversao === "TRATAMENTO_ISOLADO") {
      handleTratamentoIsolado();
    } else if (tipoConversao === "AGENDAMENTO_DIRETO") {
      handleAgendamentoDireto();
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleString("pt-PT");
  };

  const getButtonText = () => {
    switch (tipoConversao) {
      case "PROCEDIMENTO":
        return "Ir para Criar Avaliação";
      case "TRATAMENTO_ISOLADO":
        return "Confirmar e Criar";
      case "AGENDAMENTO_DIRETO":
        return "Confirmar e Agendar";
      default:
        return "Confirmar";
    }
  };

  if (fetchLoading) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (!agendamento) {
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogContent>
          <Alert severity="error">Agendamento não encontrado.</Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Fechar</Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          Confirmar Agendamento Provisório
          <div className="modal-subtitle">
            Cliente: <strong>{agendamento.cliente.nome}</strong>
          </div>
        </DialogTitle>

        <DialogContent>
          {/* Informações do Agendamento */}
          <div className="info-section">
            <h3>Informações</h3>
            <div className="info-grid">
              <div className="info-cliente">
                <Avatar
                  src={
                    agendamento.cliente.foto
                      ? `${process.env.REACT_APP_BACKEND_LINK}/${agendamento.cliente.foto}`
                      : "/images/fallBackPicture.png"
                  }
                  alt={agendamento.cliente.nome}
                  sx={{ width: 50, height: 50 }}
                />
                <div>
                  <strong>{agendamento.cliente.nome}</strong>
                  <div style={{ fontSize: "14px", color: "#666" }}>
                    {agendamento.cliente.contacto}
                  </div>
                </div>
              </div>

              <div>
                <strong>Estado:</strong>
                <Chip
                  label={agendamento.status}
                  size="small"
                  color={
                    agendamento.status === "CONFIRMADO" ? "success" : "warning"
                  }
                  style={{ marginLeft: "8px" }}
                />
              </div>

              <div>
                <strong>Data Original:</strong>
                <span>{formatDate(agendamento.data_inicio_execucao)}</span>
              </div>

              {agendamento.chamadas?.primeira && (
                <div>
                  <strong>1ª Chamada:</strong>
                  <span>{formatDate(agendamento.chamadas.primeira.data)}</span>
                </div>
              )}

              {agendamento.chamadas?.segunda && (
                <div>
                  <strong>2ª Chamada:</strong>
                  <span>{formatDate(agendamento.chamadas.segunda.data)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Data/Hora do Agendamento Real */}
          <div className="datetime-section">
            <h3>Data/Hora do Agendamento Real</h3>
            <div style={{ display: "flex", gap: "16px" }}>
              <DateTimePicker
                label="Data/Hora Início"
                value={dataAgendamento}
                onChange={setDataAgendamento}
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" />
                )}
                ampm={false}
              />

              <DateTimePicker
                label="Data/Hora Fim"
                value={dataFimAgendamento}
                onChange={setDataFimAgendamento}
                renderInput={(params) => (
                  <TextField {...params} fullWidth margin="normal" />
                )}
                ampm={false}
              />
            </div>
          </div>

          {/* Tipo de Conversão */}
          <FormControl component="fieldset" fullWidth margin="normal">
            <FormLabel component="legend">
              <h3 style={{ margin: "16px 0 8px 0" }}>Tipo de Conversão</h3>
            </FormLabel>
            <RadioGroup
              value={tipoConversao}
              onChange={(e) => setTipoConversao(e.target.value)}
            >
              <FormControlLabel
                value="PROCEDIMENTO"
                control={<Radio />}
                label="Criar Avaliação Agora"
              />
              <FormControlLabel
                value="TRATAMENTO_ISOLADO"
                control={<Radio />}
                label="Tratamento Isolado"
              />
              <FormControlLabel
                value="AGENDAMENTO_DIRETO"
                control={<Radio />}
                label="Agendar Avaliação"
              />
            </RadioGroup>
          </FormControl>

          {/* Campos Dinâmicos */}
          <div className="dynamic-fields">
            {tipoConversao === "PROCEDIMENTO" && (
              <>
                <Alert severity="info" style={{ marginTop: "16px" }}>
                  Você será redirecionado para a página de criação de avaliação.
                  Lá será possível criar avaliação e orçamento completos.
                </Alert>

                <TextField
                  label="Observações (opcional)"
                  multiline
                  rows={3}
                  fullWidth
                  margin="normal"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                />
              </>
            )}

            {tipoConversao === "TRATAMENTO_ISOLADO" && (
              <>
                <FormControl fullWidth margin="normal">
                  <InputLabel>
                    {adicionarEntrada || usaTratamentos
                      ? "Selecione o Tratamento*"
                      : "Selecione a Categoria*"}
                  </InputLabel>
                  <Select
                    value={procedimentoSelecionado}
                    onChange={(e) => setProcedimentoSelecionado(e.target.value)}
                    label={
                      adicionarEntrada || usaTratamentos
                        ? "Selecione o Tratamento*"
                        : "Selecione a Categoria*"
                    }
                  >
                    <MenuItem value="">
                      {adicionarEntrada || usaTratamentos
                        ? "Selecione um tratamento"
                        : "Selecione uma categoria"}
                    </MenuItem>
                    {adicionarEntrada || usaTratamentos
                      ? tratamentos.map((trat) => (
                          <MenuItem key={trat._id} value={trat._id}>
                            {trat.designacao}
                            {trat.fornecedor?.nome
                              ? ` (${trat.fornecedor.nome})`
                              : ""}
                          </MenuItem>
                        ))
                      : categorias.map((cat) => (
                          <MenuItem key={cat._id} value={cat._id}>
                            {cat.categoria}
                          </MenuItem>
                        ))}
                  </Select>
                </FormControl>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={adicionarEntrada}
                      onChange={(e) => {
                        const novoValor = e.target.checked;
                        setAdicionarEntrada(novoValor);

                        // Só limpa se clínica usa CATEGORIA
                        // Porque vai trocar entre tratamentos e categorias
                        if (!usaTratamentos) {
                          setProcedimentoSelecionado("");
                        }
                      }}
                    />
                  }
                  label="Adicionar Entrada (Pagamento)"
                />

                <TextField
                  label="Observações (opcional)"
                  multiline
                  rows={3}
                  fullWidth
                  margin="normal"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                />
              </>
            )}

            {tipoConversao === "AGENDAMENTO_DIRETO" && (
              <>
                <Alert severity="info" style={{ marginTop: "16px" }}>
                  Será criado um agendamento de avaliação na agenda do médico. A
                  avaliação será realizada posteriormente.
                </Alert>

                <TextField
                  label="Observações (opcional)"
                  multiline
                  rows={3}
                  fullWidth
                  margin="normal"
                  value={observacoes}
                  onChange={(e) => setObservacoes(e.target.value)}
                />
              </>
            )}
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {getButtonText()}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
}

export default ModalConfirmarAgendamento;
